import { Direction, PassengerType } from "@/models/Enums";
import { combinations } from "@/modules/Quickform/config/combinations";
import { routes } from "@/modules/Quickform/config/routes";
import { MultiLegDepartureDetails } from "@/modules/Quickform/models/MultiLegForm";
import { DepartureRequest, Resource } from "@/modules/Quickform/models/QuickformApi";
import { QuickformState } from "@/modules/Quickform/models/QuickformStates";
import { VehicleItem } from "@/modules/Quickform/models/Vehicle";
import { usePassengerStore } from "@/store/passengerStore";
import { addDays } from "date-fns";
import { MMDDYYYY, dateFormat, dayMonth } from "./dateHelpers";

export const getReturnRoutes = (outwardRoute: string) => {
    const combination = combinations.find((c) => c.value === outwardRoute);
    if (combination) {
        const returnRoutes = routes.filter((r) => {
            return combination.combinations.find((c) => c === r.value);
        });

        return returnRoutes;
    } else return [];
};

export const getNextValidDate = (date: Date, disabledDates?: Date[]) => {
    if (!disabledDates) return date;
    let newDate = date;
    while (disabledDates.some((disabledDate) => MMDDYYYY(disabledDate) === MMDDYYYY(newDate))) {
        newDate = addDays(newDate, 1);
    }
    return newDate;
};

export const getDefaultReturn = (outward: string) => {
    const start = outward.slice(0, outward.length / 2);
    const end = outward.slice(outward.length / 2, outward.length);
    return routes.find((r) => r.value === end + start)?.value;
};

export const isValidPassengers = (hasReturn?: boolean) => {
    const passengerStore = usePassengerStore();

    const outwardPassengers = passengerStore.getPassengers(Direction.Outward);
    const returnPassengers = passengerStore.getPassengers(Direction.Return);
    const differentDetails = passengerStore.getDifferentDetails;

    const validTypes = [PassengerType.Adult, PassengerType.AdultConcession];
    let valid = false;

    valid = outwardPassengers.some((p) => validTypes.includes(p.passengerType));

    if (valid && differentDetails && hasReturn) {
        valid = returnPassengers.some((p) => validTypes.includes(p.passengerType));
    }

    return valid;
};

export const isValidDepartures = (departure: MultiLegDepartureDetails[]) => {
    return departure.every((obj, index) => {
        if (index === 0 || !obj.date) {
            return true;
        }
        const previousDate = departure[index - 1].date;
        if (!previousDate) return true;
        return obj.date >= previousDate;
    });
};

export const getDatesPlaceholder = (outwardDate?: Date, returnDate?: Date, hasReturn?: boolean) => {
    if (!hasReturn) {
        return outwardDate ? `${dayMonth(outwardDate)}` : "Select date";
    } else {
        if (outwardDate && !returnDate) return `${dayMonth(outwardDate)} -`;
        if (outwardDate && returnDate) return `${dayMonth(outwardDate)} - ${dayMonth(returnDate)}`;
    }
    return "Select dates";
};

export const createRequest = (state: QuickformState, passengers: Resource[], vehicles: VehicleItem[], hasReturn?: boolean): DepartureRequest => {
    if (!state.outwardDate) return {} as DepartureRequest;

    return {
        date: hasReturn && state.returnDate ? dateFormat(state.returnDate) : dateFormat(state.outwardDate),
        route: hasReturn ? state.returnRoute : state.outwardRoute,
        resources: passengers,
        vehicles: vehicles,
    };
};

export const getQuickformState = (departures: DepartureRequest[]): QuickformState => {
    const state: QuickformState = {} as QuickformState;

    const outwardDeparture = departures[0];
    if (outwardDeparture) {
        state.outwardRoute = outwardDeparture.route;
        state.outwardDate = new Date(outwardDeparture.date);
    }

    const returnDeparture = departures[1] ?? undefined;
    if (returnDeparture) {
        state.returnRoute = returnDeparture.route;
        state.returnDate = new Date(returnDeparture.date);
    }

    return state;
};
